import { useEffect } from "react";

const DEFAULT_TITLE = "BigCircle | Food Delivery & Local Restaurants";
const DEFAULT_DESCRIPTION =
  "Find nearby restaurants, browse menus, and order food for delivery with BigCircle.";
const DEFAULT_SOCIAL_IMAGE = "/og-image.png";

const cleanText = (value, fallback = "") =>
  String(value || fallback)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const absoluteUrl = (value, origin) => {
  if (!value) return new URL(DEFAULT_SOCIAL_IMAGE, `${origin}/`).toString();
  try {
    return new URL(value, origin).toString();
  } catch {
    return new URL(DEFAULT_SOCIAL_IMAGE, `${origin}/`).toString();
  }
};

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    const match = selector.match(/meta\[(name|property)="([^"]+)"\]/);
    if (match) element.setAttribute(match[1], match[2]);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
};

const Seo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path,
  image,
  imageAlt,
  type = "website",
  robots = "index, follow, max-image-preview:large",
  structuredData,
}) => {
  useEffect(() => {
    const origin = String(
      process.env.REACT_APP_SITE_URL || window.location.origin
    ).replace(/\/$/, "");
    const canonical = new URL(
      path || window.location.pathname,
      `${origin}/`
    ).toString();
    const safeTitle = cleanText(title, DEFAULT_TITLE);
    const safeDescription = cleanText(description, DEFAULT_DESCRIPTION).slice(
      0,
      160
    );
    const socialImage = absoluteUrl(image, origin);
    const socialImageAlt = cleanText(imageAlt, safeTitle);

    document.title = safeTitle;
    setMeta('meta[name="description"]', "content", safeDescription);
    setMeta('meta[name="robots"]', "content", robots);
    // A static Google-specific index directive must not override route noindex.
    document.head.querySelector('meta[name="googlebot"]')?.remove();
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:title"]', "content", safeTitle);
    setMeta('meta[property="og:description"]', "content", safeDescription);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", socialImage);
    setMeta('meta[property="og:image:secure_url"]', "content", socialImage);
    setMeta('meta[property="og:image:alt"]', "content", socialImageAlt);
    if (!image) {
      setMeta('meta[property="og:image:type"]', "content", "image/png");
      setMeta('meta[property="og:image:width"]', "content", "551");
      setMeta('meta[property="og:image:height"]', "content", "472");
    } else {
      document.head.querySelector('meta[property="og:image:type"]')?.remove();
      document.head.querySelector('meta[property="og:image:width"]')?.remove();
      document.head.querySelector('meta[property="og:image:height"]')?.remove();
    }
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", safeTitle);
    setMeta('meta[name="twitter:description"]', "content", safeDescription);
    setMeta('meta[name="twitter:image"]', "content", socialImage);
    setMeta('meta[name="twitter:image:alt"]', "content", socialImageAlt);

    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonical);

    const scriptId = "route-structured-data";
    document.getElementById(scriptId)?.remove();
    if (structuredData) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(structuredData).replace(
        /</g,
        "\\u003c"
      );
      document.head.appendChild(script);
    }
  }, [description, image, imageAlt, path, robots, structuredData, title, type]);

  return null;
};

export default Seo;
